import { Fragment, useEffect, useState } from 'react';
import { X, Phone, Mail, MapPin, Loader2, Link2, AlertCircle, ChevronRight, MessageSquare, Trash2, ShieldAlert, Minus, Plus, Truck, ShoppingBag } from 'lucide-react';
import ZpetTlacitko from '../ui/ZpetTlacitko';
import ModalShell from '../ui/ModalShell';
import UctenkaObjednavky from './UctenkaObjednavky';
import PrubehObjednavky from './PrubehObjednavky';
import { Receipt } from 'lucide-react';
import Dropdown from '../receptura/Dropdown';
import usePotvrzeni from '../ui/usePotvrzeni';
import {
    SECTION_COLOR, CERVENA, KANALY, fmtKc, fmtCas, fmtDen, fmtDatum, platbaPopis, StavOdznak, Nadpis,
    casHistorie, jePoSlibu, barvaSlibu,
} from './ui';
import { kategorie as kategorieStavu, popisStavu } from './stavy';

/**
 * Objednávky → detail objednávky. Norma §1: druhý sloupec vedle seznamu
 * (dřív modal přes celou stránku — z objednávky do objednávky se muselo
 * zavírat). Na mobilu celá obrazovka se šipkou zpět.
 *
 * Běžně se tu needituje: objednávky z Objedname se mění v Objedname, účty
 * z POS na pokladně. Výjimka je sekce „Ruční zásah" jen pro admina
 * (`muze_zasahovat` ze serveru) — přepsat stav nebo objednávku smazat.
 */
export default function ObjednavkaDetail({ id, obnova = 0, isDark, isMobile, mutedColor, onZpet, onZakaznik, onZmena, onSmazano, rezervaDole = null }) {
    const [uctenka, setUctenka] = useState(false);
    const [data, setData] = useState(null);
    const [chyba, setChyba] = useState(null);
    const [potvrdit, potvrzeni] = usePotvrzeni(isDark);

    // `obnova` = živá změna (Objednávky → Živě): načíst znovu potichu, bez
    // „Načítám…" — detail se nemá při každé změně stavu rozblikat.
    useEffect(() => {
        let platne = true;
        if (!obnova) { setData(null); setChyba(null); }
        fetch(`/api/objednavky/${id}`, { headers: { Accept: 'application/json' } })
            .then(r => (r.ok ? r.json() : Promise.reject(new Error(r.status === 404 ? 'Objednávka neexistuje.' : 'Objednávku se nepodařilo načíst.'))))
            .then(d => { if (platne) setData(d); })
            .catch(e => { if (platne && !obnova) setChyba(e.message); });
        return () => { platne = false; };
    }, [id, obnova]);

    const cara = isDark ? 'rgba(255,255,255,0.07)' : 'var(--border-subtle)';
    const muted = mutedColor;
    const odsazeni = isMobile ? '14px 16px' : '16px 22px';
    const sekce = { padding: odsazeni, borderTop: `1px solid ${cara}` };

    if (chyba) return <p className="text-sm text-center" style={{ color: muted, padding: 40 }}>{chyba}</p>;
    if (!data) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0', color: muted }}>
                <Loader2 size={24} className="animate-spin" />
            </div>
        );
    }

    const z = data.zakaznik || {};
    const o = { ...data, kategorie: data.kategorie || kategorieStavu(data.stav) };
    const historie = Array.isArray(data.historie_stavu) ? data.historie_stavu : Object.values(data.historie_stavu || {});
    const dph = Array.isArray(data.dph_rozpis) ? data.dph_rozpis : Object.values(data.dph_rozpis || {});
    const celkem = Number(data.celkem_s_dph) || 0;
    const body = Number(data.placeno_body_kc) || 0;
    const tip = Number(data.spropitne) || 0;
    const v = data.vcasnost;
    const pozde = jePoSlibu(o, Date.now());

    return (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            {uctenka && (
                <ModalShell open onClose={() => setUctenka(false)} isDark={isDark} nadpis="Účtenka" ikona={Receipt} barva={SECTION_COLOR}>
                    <div style={{ padding: 16 }}>
                        <UctenkaObjednavky id={id} sirka={Math.min(360, (typeof window !== 'undefined' ? window.innerWidth : 390) - 32)} isDark={isDark} />
                    </div>
                </ModalShell>
            )}
            {/* Hlavička — drží se na místě */}
            <div style={{ padding: isMobile ? '6px 16px 12px' : '16px 22px 14px', flexShrink: 0 }}>
                {isMobile && <ZpetTlacitko onClick={onZpet} popis="Zpět na objednávky" sklo isDark={isDark} style={{ marginBottom: 8 }} />}
                <div className="flex items-start" style={{ gap: 12 }}>
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center flex-wrap" style={{ gap: 8 }}>
                            <h2 style={{ margin: 0, fontSize: isMobile ? 19 : 18, fontWeight: 700, color: 'var(--text-primary)' }}>
                                {data.cislo_denni ? `#${data.cislo_denni}` : `#${data.cislo_objednavky || data.id}`}
                            </h2>
                            <StavOdznak o={o} isDark={isDark} />
                        </div>
                        <p style={{ margin: '3px 0 0', fontSize: 12.5, color: muted }}>
                            {KANALY[data.kanal] || data.kanal} · {fmtDen(data.datum, true)} {fmtCas(data.cas)}
                            {data.cislo_denni && data.cislo_objednavky ? ` · ${data.cislo_objednavky}` : ''}
                            {data.pos_nazev_uctu ? ` · ${data.pos_nazev_uctu}` : ''}
                        </p>
                    </div>
                    {isMobile && (
                        <button type="button" onClick={() => setUctenka(true)} title="Účtenka" aria-label="Účtenka"
                            className="flex items-center justify-center rounded-lg"
                            style={{ height: 36, padding: '0 12px', gap: 6, flexShrink: 0, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', background: 'transparent', border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}` }}>
                            <Receipt size={15} /> Účtenka
                        </button>
                    )}
                    {!isMobile && (
                        <button type="button" onClick={onZpet} title="Zpět na přehled" aria-label="Zpět na přehled"
                            className="flex items-center justify-center rounded-lg"
                            style={{ width: 34, height: 34, flexShrink: 0, cursor: 'pointer', color: 'var(--text-secondary)', background: 'transparent', border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}` }}>
                            <X size={16} />
                        </button>
                    )}
                </div>
            </div>

            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                {/* Zákazník */}
                <div style={sekce}>
                    <Nadpis muted={muted}>Zákazník</Nadpis>
                    {!z.id && <p className="text-[13px]" style={{ color: muted, margin: 0 }}>Bez zákazníka — objednávka nemá telefon ani účet.</p>}
                    {z.id && (
                        <div className="flex flex-col" style={{ gap: 6, fontSize: 13.5 }}>
                            <b style={{ color: 'var(--text-primary)' }}>{z.jmeno || 'bez jména'}</b>
                            {z.telefon && (
                                <a href={`tel:${z.telefon}`} className="flex items-center" style={{ gap: 7, color: SECTION_COLOR, textDecoration: 'none' }}>
                                    <Phone size={13} /> {z.telefon}
                                </a>
                            )}
                            {z.email && <span className="flex items-center truncate" style={{ gap: 7, color: 'var(--text-secondary)' }}><Mail size={13} style={{ flexShrink: 0 }} /> {z.email}</span>}
                            {onZakaznik && (
                                <button type="button" onClick={() => onZakaznik(z.id)} className="flex items-center text-left w-full"
                                    style={{ marginTop: 6, gap: 10, minHeight: 44, padding: '8px 12px', borderRadius: 10, cursor: 'pointer', color: 'var(--text-primary)', background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.025)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}` }}>
                                    <span className="flex-1 min-w-0">
                                        <span style={{ display: 'block', fontSize: 13, fontWeight: 600 }}>Karta zákazníka</span>
                                        {data.zakaznik_souhrn && (
                                            <span style={{ display: 'block', fontSize: 11.5, color: muted }}>
                                                {data.zakaznik_souhrn.pocet} objednávek · {fmtKc(data.zakaznik_souhrn.utrata)}
                                                {data.zakaznik_souhrn.prvni ? ` · od ${fmtDatum(data.zakaznik_souhrn.prvni)}` : ''}
                                            </span>
                                        )}
                                    </span>
                                    <ChevronRight size={16} style={{ color: muted }} />
                                </button>
                            )}
                        </div>
                    )}
                    {data.poznamka && (
                        <div className="flex" style={{ gap: 8, marginTop: 10, padding: '8px 10px', borderRadius: 8, fontSize: 13, color: 'var(--text-primary)', background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)' }}>
                            <MessageSquare size={14} style={{ color: muted, flexShrink: 0, marginTop: 2 }} />
                            <span>{data.poznamka}</span>
                        </div>
                    )}
                </div>

                {/* Rozvoz */}
                {data.kanal === 'rozvoz' && (
                    <div style={sekce}>
                        <Nadpis muted={muted}>Doručení</Nadpis>
                        <div className="flex flex-col" style={{ gap: 6, fontSize: 13 }}>
                            {data.adresa_doruceni && (
                                <span className="flex" style={{ gap: 7, color: 'var(--text-primary)' }}>
                                    <MapPin size={13} style={{ flexShrink: 0, marginTop: 3, color: muted }} /> {data.adresa_doruceni}
                                </span>
                            )}
                            <div className="grid" style={{ gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, minmax(0, 1fr))', gap: '10px 14px', marginTop: 4 }}>
                                <Hodnota popis="Slíbeno" hodnota={data.cas_doruceni_slibeny?.slice(0, 5) || '—'} muted={muted} cervene={pozde} />
                                <Hodnota popis="Hotová v" hodnota={v?.cas_hotova || '—'} muted={muted} />
                                {/* Dvě různé otázky: do 60 min = výkon firmy, proti slibu = férovost k zákazníkovi */}
                                <Hodnota popis="Do 60 min" muted={muted}
                                    hodnota={data.casovka ? 'časovka' : v ? `${v.minut_celkem} min ${v.do60_ok ? '✓' : '✗'}` : '—'}
                                    cervene={v?.do60_ok === false} />
                                <Hodnota popis="Proti slibu" muted={muted}
                                    hodnota={v?.slibeny_rozdil == null ? '—' : v.slibeny_rozdil <= 0 ? `${Math.abs(v.slibeny_rozdil)} min dřív` : `+${v.slibeny_rozdil} min`}
                                    barva={barvaSlibu(v?.slibeny_stav)} />
                            </div>
                            {data.casovka && (
                                <span style={{ fontSize: 11.5, color: muted }}>
                                    Objednáno na čas — do 60 minut se nepočítá, slib má toleranci {data.tolerance_slibu ?? 15} min{v ? ` (doručeno za ${v.minut_celkem} min od objednání)` : ''}.
                                </span>
                            )}
                            {data.rozvozce_jmeno && <span style={{ color: muted, marginTop: 2 }}>Rozvozce: <b style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{data.rozvozce_jmeno}</b></span>}
                        </div>
                    </div>
                )}

                {/* Položky */}
                <div style={sekce}>
                    <Nadpis muted={muted}>Položky</Nadpis>
                    {(data.polozky || []).map((p, i) => (
                        <Polozka key={p.id || i} p={p} bistro={data.kanal === 'bistro'} mapovani={data.zdroj_sync === 'scraper'} cara={cara} muted={muted} isMobile={isMobile} />
                    ))}
                    <div className="flex justify-between items-baseline" style={{ paddingTop: 10 }}>
                        <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>Celkem</span>
                        <span style={{ fontSize: 17, fontWeight: 800, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{fmtKc(celkem)}</span>
                    </div>
                </div>

                {/* Platba a DPH */}
                <div style={sekce}>
                    <Nadpis muted={muted}>Platba</Nadpis>
                    <div className="grid" style={{ gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, minmax(0, 1fr))', gap: '10px 14px' }}>
                        <Hodnota popis="Způsob" hodnota={platbaPopis(data.typ_platby)} muted={muted} />
                        <Hodnota popis="Zaplaceno" hodnota={fmtKc(Math.max(0, celkem - body))} muted={muted} />
                        <Hodnota popis="Body" hodnota={body > 0 ? fmtKc(body) : '—'} muted={muted} />
                        <Hodnota popis="Spropitné" hodnota={tip > 0 ? fmtKc(tip) : '—'} muted={muted} />
                    </div>
                    {dph.length > 0 && (
                        <div style={{ marginTop: 12 }}>
                            {dph.map((r, i) => (
                                <div key={i} className="flex justify-between" style={{ fontSize: 12, padding: '4px 0', borderBottom: `1px solid ${cara}`, gap: 10 }}>
                                    <span style={{ color: muted }}>{r.sazba === '0%' ? 'Spropitné (0 %)' : `DPH ${r.sazba}`}</span>
                                    <span style={{ color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>
                                        {fmtKc(r.zaklad)} + {fmtKc(r.dph)} = <b style={{ color: 'var(--text-primary)' }}>{fmtKc(r.celkem)}</b>
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Historie stavů */}
                {historie.length > 0 && (
                    <div style={sekce}>
                        <Nadpis muted={muted}>Historie stavů</Nadpis>
                        <div className="flex flex-col" style={{ gap: 0 }}>
                            {historie.map((h, i) => {
                                const ms = casHistorie(h.datum);
                                const storno = kategorieStavu(h.stav) === 'storno';
                                return (
                                    <div key={i} className="flex items-center" style={{ gap: 10, minHeight: 28, fontSize: 12.5 }}>
                                        <span style={{ width: 8, height: 8, borderRadius: 9, flexShrink: 0, background: storno ? CERVENA : (i === historie.length - 1 ? SECTION_COLOR : (isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.18)')) }} />
                                        <span style={{ color: muted, width: 44, fontVariantNumeric: 'tabular-nums' }}>{ms ? new Date(ms).toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                                        <span style={{ color: 'var(--text-primary)', flex: 1, minWidth: 0 }} className="truncate">{popisStavu(h.stav)}</span>
                                        {h.kdo && <span className="truncate" style={{ color: muted, maxWidth: '40%' }}>{h.kdo}</span>}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                <PrubehObjednavky id={id} obnova={obnova} sekce={sekce} muted={muted} isDark={isDark} />

                <div style={{ ...sekce, fontSize: 12, color: muted }}>
                    Zdroj:<b style={{ color: 'var(--text-secondary)' }}>{data.zdroj_sync === 'pos' ? 'POS (pokladna)' : 'Objedname'}</b>
                    {data.synced_at ? ` · synchronizováno ${new Date(data.synced_at).toLocaleString('cs-CZ', { day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' })}` : ''}
                </div>
                {data.muze_zasahovat && (
                    <RucniZasah data={data} sekce={sekce} muted={muted} isDark={isDark} potvrdit={potvrdit}
                        onUlozeno={(d) => { setData(d); onZmena?.(); }} onSmazano={onSmazano} />
                )}
                {rezervaDole && <div aria-hidden="true" style={{ height: rezervaDole }} />}
            </div>
            {potvrzeni}
        </div>
    );
}

// Co který stav udělá — nabídku (podle zdroje) posílá server v `rucni_stavy`.
const NAPOVEDA_STAVU = {
    'nová': 'Čeká na přijetí na pokladně — kuchyň ji nevidí',
    'potvrzená': 'Přijatá u nás — jde do kuchyně',
    'přijatá': 'Odeslaná do kuchyně',
    'odložená': 'Časovka — do kuchyně až v nastavený čas',
    'hotová': 'Předaná zákazníkovi — zmizí z kuchyně i fronty',
    'stornovaná': 'Zrušená — nepočítá se do tržeb',
};
const OBJEDNAME = '__objedname__';
const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content;

/**
 * Admin: přepsat stav, vrátit stav Objedname, smazat. Server hlídá totéž
 * (`isAdmin`), tady jde jen o to tlačítka neukazovat ostatním.
 */
function RucniZasah({ data, sekce, muted, isDark, potvrdit, onUlozeno, onSmazano }) {
    const [pracuji, setPracuji] = useState(false);
    const [chyba, setChyba] = useState(null);
    const pos = data.zdroj_sync === 'pos';
    const zamceno = !!data.stav_rucne_v;
    const nazev = data.cislo_denni ? `#${data.cislo_denni}` : `#${data.cislo_objednavky || data.id}`;

    const stavy = data.rucni_stavy || [];
    const volby = [
        ...(zamceno && !pos ? [{ value: OBJEDNAME, label: 'Podle Objedname', popis: 'zrušit ruční stav' }] : []),
        ...stavy.map(s => ({ value: s, label: popisStavu(s), popis: NAPOVEDA_STAVU[s] })),
    ];

    const odeslat = async (url, init) => {
        setPracuji(true);
        setChyba(null);
        try {
            const r = await fetch(url, {
                ...init,
                headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrf(), ...(init.headers || {}) },
            });
            const j = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(j.message || 'Akce se nepovedla.');
            return j;
        } catch (e) {
            setChyba(e.message);
            return null;
        } finally {
            setPracuji(false);
        }
    };

    const zmenitStav = async (v) => {
        if (v === data.stav) return;
        const stav = v === OBJEDNAME ? null : v;
        const d = await odeslat(`/api/objednavky/${data.id}/stav`, { method: 'PATCH', body: JSON.stringify({ stav }) });
        if (d) onUlozeno(d);
    };

    const smazat = async () => {
        const text = [
            'Objednávka zmizí z přehledů, tržeb i z karty zákazníka. Kopie zůstane v záznamu smazaných.',
            !pos ? 'Ze synchronizace Objedname se už znovu nenačte.' : null,
            data.kategorie === 'otevrena' ? 'Je to otevřený účet — pokud ho má někdo otevřený na pokladně, zmizí mu.' : null,
            data.ma_pohyb_pokladny ? 'Hotovost už je zaúčtovaná v pokladně — ten pohyb zůstane, oprav ho v Pokladně.' : null,
        ].filter(Boolean).join('\n\n');
        if (!(await potvrdit(`Smazat objednávku ${nazev}?`, { text, potvrdit: 'Smazat' }))) return;
        const d = await odeslat(`/api/objednavky/${data.id}`, { method: 'DELETE' });
        if (d) onSmazano?.();
    };

    return (
        <div style={sekce}>
            <Nadpis muted={muted}>Ruční zásah · admin</Nadpis>
            <div className="flex flex-col" style={{ gap: 10 }}>
                <div>
                    <span style={{ display: 'block', fontSize: 11, color: muted, marginBottom: 4 }}>Stav objednávky</span>
                    <Dropdown value={stavy.includes(data.stav) ? data.stav : null} onChange={zmenitStav}
                        options={volby} placeholder={popisStavu(data.stav)} isDark={isDark} accent={SECTION_COLOR} />
                </div>
                {zamceno && (
                    <p className="flex" style={{ gap: 7, margin: 0, fontSize: 12, color: muted }}>
                        <ShieldAlert size={14} style={{ flexShrink: 0, marginTop: 1, color: '#F59E0B' }} />
                        <span>
                            Stav ručně nastavil {data.stav_rucne_kdo || 'admin'} {new Date(data.stav_rucne_v).toLocaleString('cs-CZ', { day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' })}.
                            {!pos && ' Synchronizace z Objedname ho nepřepíše, dokud nezvolíš „Podle Objedname".'}
                        </span>
                    </p>
                )}
                {data.lze_upravit_admin && (
                    <UpravaUctu data={data} muted={muted} isDark={isDark} pracuji={pracuji} odeslat={odeslat} onUlozeno={onUlozeno} />
                )}
                <button type="button" onClick={smazat} disabled={pracuji} className="flex items-center justify-center rounded-lg"
                    style={{ gap: 8, height: 40, fontSize: 13, fontWeight: 600, cursor: pracuji ? 'wait' : 'pointer', color: CERVENA, background: 'transparent', border: `1px solid ${isDark ? 'rgba(239,68,68,0.35)' : 'rgba(239,68,68,0.4)'}` }}>
                    {pracuji ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />} Smazat objednávku
                </button>
                {chyba && <p style={{ margin: 0, fontSize: 12, color: CERVENA }}>{chyba}</p>}
            </div>
        </div>
    );
}

/**
 * Admin: úprava nezaplaceného pokladního účtu — způsob dodání a položky.
 * Pokladna objednávku připravenou k výdeji („v teple") už nemění; tady to
 * jde, kdyby se něco pokazilo. Server hlídá admina a nezaplacený účet.
 */
function UpravaUctu({ data, muted, isDark, pracuji, odeslat, onUlozeno }) {
    const [adresa, setAdresa] = useState(data.adresa_doruceni || data.zakaznik?.adresa || '');
    const rozvoz = data.kanal === 'rozvoz';
    const polozky = (data.polozky || []).filter(p => !p.stornovano_v && p.typ !== 'doprava');
    const ramecek = isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)';
    const tl = (aktivni) => ({
        height: 40, padding: '0 14px', fontSize: 13, fontWeight: 600, borderRadius: 8, cursor: pracuji ? 'wait' : 'pointer',
        display: 'flex', alignItems: 'center', gap: 6,
        background: aktivni ? 'var(--section-tint, rgba(245,158,11,0.14))' : 'transparent',
        border: `1px solid ${aktivni ? '#F59E0B' : ramecek}`, color: 'var(--text-primary)',
    });

    const dodani = async (typ) => {
        const d = await odeslat(`/api/objednavky/${data.id}/dodani`, { method: 'PATCH', body: JSON.stringify({ typ, adresa: typ === 'rozvoz' ? adresa : null }) });
        if (d) onUlozeno(d);
    };
    const pocet = async (p, novy) => {
        if (novy < 1) return;
        const d = await odeslat(`/api/objednavky/${data.id}/polozky/${p.id}`, { method: 'PATCH', body: JSON.stringify({ pocet: novy }) });
        if (d) onUlozeno(d);
    };
    const odebrat = async (p) => {
        const d = await odeslat(`/api/objednavky/${data.id}/polozky/${p.id}`, { method: 'DELETE' });
        if (d) onUlozeno(d);
    };

    return (
        <div className="flex flex-col" style={{ gap: 10, paddingTop: 4 }}>
            {data.v_teple && (
                <p className="flex" style={{ gap: 7, margin: 0, fontSize: 12, color: muted }}>
                    <ShieldAlert size={14} style={{ flexShrink: 0, marginTop: 1, color: '#F59E0B' }} />
                    <span>Připravená k výdeji — pokladna ji už nemění, tady ano (dokud není zaplacená).</span>
                </p>
            )}
            <div>
                <span style={{ display: 'block', fontSize: 11, color: muted, marginBottom: 4 }}>Způsob dodání</span>
                <div className="flex flex-wrap" style={{ gap: 6 }}>
                    <button type="button" disabled={pracuji} onClick={() => !rozvoz || dodani('sebou')} style={tl(data.kanal === 'vydej')}>
                        <ShoppingBag size={15} /> S sebou
                    </button>
                    <button type="button" disabled={pracuji || !adresa.trim()} onClick={() => dodani('rozvoz')} style={tl(rozvoz)}>
                        <Truck size={15} /> {rozvoz ? 'Rozvoz — uložit adresu' : 'Rozvoz'}
                    </button>
                </div>
                <input value={adresa} onChange={e => setAdresa(e.target.value)} placeholder="Adresa pro rozvoz"
                    style={{ marginTop: 6, width: '100%', height: 38, padding: '0 10px', fontSize: 13, borderRadius: 8, border: `1px solid ${ramecek}`, background: 'transparent', color: 'var(--text-primary)' }} />
            </div>
            <div>
                <span style={{ display: 'block', fontSize: 11, color: muted, marginBottom: 4 }}>Položky</span>
                <div className="flex flex-col" style={{ gap: 4 }}>
                    {polozky.map(p => (
                        <div key={p.id} className="flex items-center" style={{ gap: 6 }}>
                            <span className="flex-1 truncate" style={{ fontSize: 13 }}>{p.nazev}</span>
                            <button type="button" aria-label="Ubrat kus" disabled={pracuji || p.pocet <= 1} onClick={() => pocet(p, Number(p.pocet) - 1)} style={{ ...tl(false), width: 34, height: 34, padding: 0, justifyContent: 'center' }}><Minus size={14} /></button>
                            <span style={{ minWidth: 22, textAlign: 'center', fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{Number(p.pocet)}</span>
                            <button type="button" aria-label="Přidat kus" disabled={pracuji} onClick={() => pocet(p, Number(p.pocet) + 1)} style={{ ...tl(false), width: 34, height: 34, padding: 0, justifyContent: 'center' }}><Plus size={14} /></button>
                            <button type="button" aria-label="Odebrat položku" disabled={pracuji} onClick={() => odebrat(p)} style={{ ...tl(false), width: 34, height: 34, padding: 0, justifyContent: 'center', color: CERVENA }}><Trash2 size={14} /></button>
                        </div>
                    ))}
                    {polozky.length === 0 && <span style={{ fontSize: 12, color: muted }}>Žádné položky.</span>}
                </div>
            </div>
        </div>
    );
}

function Hodnota({ popis, hodnota, muted, cervene = false, barva = null }) {
    return (
        <div className="min-w-0">
            <span style={{ display: 'block', fontSize: 11, color: muted }}>{popis}</span>
            <span className="truncate" style={{ display: 'block', fontSize: 13.5, fontWeight: 600, color: barva || (cervene ? '#F87171' : 'var(--text-primary)'), fontVariantNumeric: 'tabular-nums' }}>{hodnota}</span>
        </div>
    );
}

/** Mapování položky z Objedname na interní recepturu — jen ikona s popiskem. */
function MapovaniIkona({ p }) {
    const m = p?.mapping;
    let barva = '#F59E0B', Ikona = AlertCircle, titulek = 'Nezmapováno — bez interního protějšku';
    if (m?.cilovy_typ === 'ignorovat') { barva = 'var(--text-muted)'; Ikona = X; titulek = 'Ignorováno'; }
    else if (m?.cilovy_typ === 'recept' && m.recept) { barva = '#059669'; Ikona = Link2; titulek = `${m.recept.kod} — ${m.recept.nazev}${m.recept.varianta ? ` (${m.recept.varianta})` : ''}`; }
    else if (m?.cilovy_typ === 'recept') { barva = CERVENA; titulek = `Receptura #${m.recept_id || '?'} neexistuje`; }
    else if (m?.cilovy_typ === 'combo' && m.combo_skupina) { barva = '#059669'; Ikona = Link2; titulek = `Combo: ${m.combo_skupina.nazev}`; }
    else if (m?.cilovy_typ === 'naklad') { barva = '#059669'; Ikona = Link2; titulek = `Náklad: ${m.naklad_typ || p.typ}`; }
    return <span title={titulek} aria-label={titulek} style={{ display: 'inline-flex', color: barva, marginRight: 6, flexShrink: 0, cursor: 'help' }}><Ikona size={12} strokeWidth={2.5} /></span>;
}

function Polozka({ p, bistro, mapovani, cara, muted, isMobile }) {
    const produkt = p.typ === 'produkt' || !p.typ;
    const deti = p.children || [];
    const viditelne = bistro ? deti.filter(c => c.typ !== 'baleni') : deti;
    const cena = Number(p.celkem_s_dph) || 0;
    const baleni = deti.filter(c => c.typ === 'baleni').reduce((s, c) => s + (Number(c.celkem_s_dph) || 0), 0);
    const pridavky = deti.filter(c => c.typ === 'pridavek').reduce((s, c) => s + (Number(c.celkem_s_dph) || 0), 0);
    const cista = cena - baleni;
    const celkem = bistro ? cista + pridavky : cena + pridavky;
    const rozpad = viditelne.length > 0;

    return (
        <Fragment>
            <div className="flex justify-between items-baseline" style={{ gap: 10, padding: isMobile ? '8px 0' : '7px 0', borderBottom: rozpad ? 'none' : `1px solid ${cara}` }}>
                <span className="flex items-center min-w-0 flex-1" style={{ fontSize: 13, fontWeight: produkt ? 600 : 400, color: 'var(--text-primary)' }}>
                    {mapovani && <MapovaniIkona p={p} />}
                    <span className="truncate">{p.typ === 'doprava' ? 'Doprava' : (p.nazev || '—')}</span>
                    {produkt && Number(p.pocet) > 1 && <span style={{ color: muted, fontWeight: 400, marginLeft: 5, fontSize: 11.5 }}>×{Number(p.pocet)}</span>}
                </span>
                <span style={{ fontSize: 13.5, fontWeight: produkt ? 700 : 500, color: 'var(--text-primary)', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                    {fmtKc(produkt ? celkem : cena)}
                </span>
            </div>
            {rozpad && (
                <div style={{ paddingLeft: 16, paddingBottom: 6, borderBottom: `1px solid ${cara}` }}>
                    <div className="flex justify-between" style={{ fontSize: 11.5, color: muted, padding: '2px 0' }}>
                        <span>Produkt</span><span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtKc(cista)}</span>
                    </div>
                    {viditelne.map((c, i) => (
                        <div key={c.id || i} className="flex justify-between" style={{ fontSize: 11.5, color: muted, padding: '2px 0', gap: 8 }}>
                            <span className="flex items-center min-w-0 flex-1">
                                {mapovani && <MapovaniIkona p={c} />}
                                <span className="truncate">{c.typ === 'baleni' ? 'Obal: ' : (Number(c.pocet) < 0 ? '− ' : '+ ')}{c.nazev}{Math.abs(Number(c.pocet)) > 1 ? ` ×${Math.abs(Number(c.pocet))}` : ''}</span>
                            </span>
                            <span style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{fmtKc(Number(c.celkem_s_dph) || 0)}</span>
                        </div>
                    ))}
                </div>
            )}
        </Fragment>
    );
}
